import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useEnterpriseAuth as useAuth } from './EnterpriseAuthContext';
import { apiUtils } from '../utils/apiUtils';
import { useNotification } from './NotificationContext';
import { useRealtimeConnection } from '../hooks/useRealtimeConnection';

const ChatContext = createContext();

export function useChat() {
  return useContext(ChatContext);
}

export function ChatProvider({ children, disabled = false }) {
  const { user, isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const typingTimeoutRef = useRef({});

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    // Skip system messages that were already handled by useWebSocket
    if (data.type === 'authenticated' || data.type === 'error') {
      return;
    }

    switch (data.type) {
      case 'connected':
        break;
        
      case 'chat_message':
        // Add new message to the channel
        const { channelId, ...message } = data.data;
        setMessages(prev => ({
          ...prev,
          [channelId]: [...(prev[channelId] || []), message],
        }));
        
        // Update channel's last message
        setChannels(prev => prev.map(ch => 
          ch.id === channelId 
            ? { ...ch, last_message: message.content, updated_at: message.timestamp }
            : ch
        ));
        
        // Show notification if not from current user
        if (message.userId !== user.id) {
          notify(`New message from ${message.userName}`, 'info');
        }
        break;
        
      case 'typing':
        // Update typing indicators
        const { userId, channelId: typingChannelId, isTyping } = data.data;
        if (userId !== user.id) {
          setTypingUsers(prev => ({
            ...prev,
            [typingChannelId]: isTyping 
              ? [...(prev[typingChannelId] || []).filter(id => id !== userId), userId]
              : (prev[typingChannelId] || []).filter(id => id !== userId),
          }));
          
          // Clear typing indicator after 3 seconds
          if (isTyping) {
            if (typingTimeoutRef.current[userId]) {
              clearTimeout(typingTimeoutRef.current[userId]);
            }
            typingTimeoutRef.current[userId] = setTimeout(() => {
              setTypingUsers(prev => ({
                ...prev,
                [typingChannelId]: (prev[typingChannelId] || []).filter(id => id !== userId),
              }));
            }, 3000);
          }
        }
        break;
        
      case 'read_receipts':
        // Update read status for messages
        const { messageIds, userId: readerId } = data.data;
        // TODO: Update message read status in UI
        break;
        
      case 'subscribed':
        break;
        
      case 'unsubscribed':
        break;
    }
  }, [user, notify]);

  // Initialize real-time connection (WebSocket only; no polling fallback)
  const {
    connected: wsConnected,
    authenticated: wsAuthenticated,
    error: wsError,
    connectionType,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    send: sendWebSocketMessage
  } = useRealtimeConnection({
    endpoint: '/api/chat/ws',
    onMessage: handleWebSocketMessage,
    onAuthenticated: (data) => {
      // Subscribe to active channel after authentication
      if (activeChannel) {
        sendWebSocketMessage({
          type: 'subscribe_channel',
          channelId: activeChannel.id,
        });
      }
    },
    maxReconnectAttempts: 3,
    enableWebSocket: !disabled
  });

  // Fetch user's channels
  const fetchChannels = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    try {
      setLoading(true);
      const response = await apiUtils.get('/api/chat/channels');
      setChannels(response.channels || []);
    } catch (error) {
      console.error('[ChatContext] Error fetching channels:', error);
      notify('Failed to load chat channels', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, notify]);

  // Create a new channel
  const createChannel = useCallback(async ({ name, type = 'patient', patientId, description, members = [] }) => {
    try {
      const response = await apiUtils.post('/api/chat/channels', {
        name,
        type,
        patientId,
        description,
        members,
      });
      
      const newChannel = response.channel;
      setChannels(prev => [newChannel, ...prev]);
      return newChannel;
    } catch (error) {
      console.error('[ChatContext] Error creating channel:', error);
      notify('Failed to create channel', 'error');
      throw error;
    }
  }, [notify]);

  // Create a patient-specific channel
  const createPatientChannel = useCallback(async (patientId, members = []) => {
    try {
      const response = await apiUtils.post('/api/chat/channels/patient', {
        patientId,
        members,
      });
      
      const channel = response.channel;
      
      // Add to channels if not already present
      setChannels(prev => {
        const exists = prev.find(c => c.id === channel.id);
        if (exists) return prev;
        return [channel, ...prev];
      });
      
      return channel;
    } catch (error) {
      console.error('[ChatContext] Error creating patient channel:', error);
      notify('Failed to create patient channel', 'error');
      throw error;
    }
  }, [notify]);

  // Set active channel and fetch its details
  const selectChannel = useCallback(async (channelId) => {
    if (!channelId) {
      setActiveChannel(null);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch channel details
      const channelResponse = await apiUtils.get(`/api/chat/channels/${channelId}`);
      setActiveChannel(channelResponse.channel);
      
      // Fetch messages if not already loaded
      if (!messages[channelId]) {
        const messagesResponse = await apiUtils.get(`/api/chat/channels/${channelId}/messages`);
        setMessages(prev => ({
          ...prev,
          [channelId]: messagesResponse.messages || [],
        }));
      }
      
      // Subscribe to channel via WebSocket
      if (wsAuthenticated) {
        sendWebSocketMessage({
          type: 'subscribe_channel',
          channelId,
        });
      }
      
      // Update channel read time
      await apiUtils.put(`/api/chat/channels/${channelId}/read`);
    } catch (error) {
      console.error('[ChatContext] Error selecting channel:', error);
      notify('Failed to load channel', 'error');
    } finally {
      setLoading(false);
    }
  }, [messages, wsAuthenticated, sendWebSocketMessage, notify]);

  // Send a message
  const sendMessage = useCallback(async (content, metadata = {}) => {
    if (!activeChannel || !content.trim()) return;

    try {
      const response = await apiUtils.post(`/api/chat/channels/${activeChannel.id}/messages`, {
        content,
        metadata,
      });
      
      const message = response.message;
      
      // Optimistically add message to local state
      setMessages(prev => ({
        ...prev,
        [activeChannel.id]: [...(prev[activeChannel.id] || []), message],
      }));
      
      return message;
    } catch (error) {
      console.error('[ChatContext] Error sending message:', error);
      notify('Failed to send message', 'error');
      throw error;
    }
  }, [activeChannel, notify]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (messageIds) => {
    if (!messageIds || messageIds.length === 0) return;

    try {
      await apiUtils.post('/api/chat/messages/read', { messageIds });
      
      // Send read receipts via WebSocket
      if (wsAuthenticated && activeChannel) {
        sendWebSocketMessage({
          type: 'mark_read',
          channelId: activeChannel.id,
          messageIds,
        });
      }
    } catch (error) {
      console.error('[ChatContext] Error marking messages as read:', error);
    }
  }, [activeChannel, wsAuthenticated, sendWebSocketMessage]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (!activeChannel || !wsAuthenticated) return;

    sendWebSocketMessage({
      type: 'typing',
      channelId: activeChannel.id,
      isTyping,
    });
  }, [activeChannel, wsAuthenticated, sendWebSocketMessage]);

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (!disabled && isAuthenticated && user?.id) {
      fetchChannels();
      connectWebSocket();
    } else {
      disconnectWebSocket();
      setChannels([]);
      setActiveChannel(null);
      setMessages({});
      setTypingUsers({});
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isAuthenticated, user?.id, disabled]);

  // Subscribe to active channel when WebSocket is authenticated
  useEffect(() => {
    if (wsAuthenticated && activeChannel) {
      sendWebSocketMessage({
        type: 'subscribe_channel',
        channelId: activeChannel.id,
      });
    }
  }, [wsAuthenticated, activeChannel, sendWebSocketMessage]);

  // Clean up typing timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
      typingTimeoutRef.current = {};
    };
  }, []);

  const value = {
    channels,
    activeChannel,
    messages,
    typingUsers,
    wsConnected,
    wsAuthenticated,
    wsError,
    connectionType,
    loading,
    fetchChannels,
    createChannel,
    createPatientChannel,
    selectChannel,
    sendMessage,
    markMessagesAsRead,
    sendTypingIndicator,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}
